import { SelectionModel } from '@angular/cdk/collections';
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { CmLocalizePipe } from '@ats/cm/core';
import { Unsubscribable } from '@ats/utility';
import { SettingsService } from '@cm/core/settings';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, skip, startWith, take, takeUntil } from 'rxjs/operators';
import {
	CmITableConfig,
	CmTableColumn,
	CmTableRow,
	CmTableRowSummary,
	CmTableSelectionChange,
} from '../../models';
import { CmTableService, SELECT_COLUMN_NAME } from '../../services/table.service';

type PageConfig = Pick<CmITableConfig, 'pageSize' | 'pageOptions'>;

@Component({
    selector: 'cm-new-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    providers: [CmTableService, MatPaginatorIntl],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CmNewTableComponent<T> extends Unsubscribable implements OnInit {
	/** Observable<CmTableRow[]> - Table data to render */
	@Input() rows$: Observable<CmTableRow[]>;
	/** Observable<CmTableColumn[]> - Column data for the table */
	@Input() columns$: Observable<CmTableColumn[]>;
	/** Observable<CmITableConfig> - Async config for the table */
	@Input() config$: Observable<CmITableConfig>;
	@Input() config: CmITableConfig = {};
	@Input() headerTpls: { [key: string]: TemplateRef<any> };
	@Input() cellTpls: { [key: string]: TemplateRef<any> };
	@Input() showTableMeta: boolean = true;
	/** Observable<boolean | undefined> - Toggles selection state of all rows.
	 * Undefined considers current selection state. */
	@Input() toggleSelection$: Observable<boolean | undefined>;
	@Output() selectChange = new EventEmitter<CmTableSelectionChange>();
	configReady$: Observable<boolean>;
	parsedColumns$: Observable<CmTableColumn[]>;
	summary$: Observable<CmTableRowSummary>;
	hasSummary$: Observable<boolean>;
	displayedColumns$: Observable<string[]>;
	datasource: MatTableDataSource<CmTableRow>;
	selectColumnName: string = SELECT_COLUMN_NAME;
	pageConfig$: Observable<PageConfig>;
	isAllSelected: () => boolean;
	masterToggle: (newState?: boolean) => void;
	trackBy: (row: CmTableRow) => any;

	@ViewChild(MatSort, { static: false }) set sort(sort: MatSort) {
		this.waiter.addViewChild(sort || 'sort');
	}

	@ViewChild(MatPaginator, { static: true }) set paginator(paginator: MatPaginator) {
		this.waiter.addViewChild(paginator || 'paginator');
	}

	constructor(
		public waiter: CmTableService,
		private readonly paginatorService: MatPaginatorIntl,
		private readonly localize: CmLocalizePipe,
		private readonly settings: SettingsService
	) {
		super();
		this.pageConfig$ = this.getPageConfig$();
	}

	get selection(): SelectionModel<any> {
		return this.waiter.selection;
	}

	get hasSelection() {
		return typeof this.waiter.selection !== 'undefined';
	}

	get selectionFilter() {
		return this.waiter.selectedFilter !== null;
	}

	get lowestConfigOptions() {
		return this.waiter?.config?.pageOptions?.sort()?.reverse()[0] || 0;
	}

	ngOnInit() {
		this.configReady$ = this.waiter.configReady$;
		if (!this.config$) {
			this.config$ = of(this.config || {});
		}
		this.rows$ = this.rows$.pipe(startWith(null));
		this.trackBy = this.waiter.trackBy;
		combineLatest([this.rows$, this.columns$, this.config$, this.pageConfig$])
			.pipe(
				filter(([rows, columns, config]) => rows !== null && !!columns && !!config),
				take(1)
			)
			.subscribe(([rows, columns, config, pageConfig]) => {
				this.waiter.setRows(rows);
				this.datasource = this.waiter.tableDatasource;
				this.waiter.initialize({ ...pageConfig, ...config });
				this.setPaginatorLocalizations();
				this.parsedColumns$ = this.waiter.updateColumns(columns);
				this.hasSummary$ = this.waiter.hasSummary$;
				this.summary$ = this.waiter.summary$;
				this.displayedColumns$ = this.waiter.displayColumns$;
				this.isAllSelected = this.waiter.isAllSelected;
				this.masterToggle = this.waiter.masterToggle;

				if ((config || {}).selectable) {
					this.waiter.selection$
						.pipe(takeUntil(this.unsub))
						.subscribe(({ added, removed, source }) =>
							this.selectChange.emit({
								added,
								removed,
								selected: source.selected,
							})
						);
				}

				if (this.toggleSelection$) {
					this.toggleSelection$
						.pipe(takeUntil(this.unsub))
						.subscribe(newState => this.masterToggle(newState));
				}
			});

		this.rows$
			.pipe(
				filter(rows => rows !== null),
				skip(1),
				takeUntil(this.unsub)
			)
			.subscribe(rows => {
				this.waiter.setRows(rows);
			});
	}

	private getPageConfig$(): Observable<PageConfig> {
		return this.settings.config$.pipe(
			map(config => {
				return { pageOptions: config.pageSizeOptions, pageSize: config.pageSize };
			}),
			takeUntil(this.unsub)
		);
	}

	private setPaginatorLocalizations() {
		this.paginatorService.getRangeLabel = (page: number, pageSize: number, length: number) => {
			const start = page === 0 ? 0 : page * pageSize;
			const end = start + pageSize > length ? length : start + pageSize;
			return `${start + 1}\u2013${end} / ${length}`;
		};
		this.paginatorService.itemsPerPageLabel = this.localize.transform(
			'Items per page:',
			'Core/System.Table.Label.PaginationOptions'
		);
		this.paginatorService.firstPageLabel = this.localize.transform(
			'First Page',
			'Core/System.Table.Button.PaginationFirstPage'
		);
		this.paginatorService.lastPageLabel = this.localize.transform(
			'Last Page',
			'Core/System.Table.Button.PaginationLastPage'
		);
		this.paginatorService.nextPageLabel = this.localize.transform(
			'Next Page',
			'Core/System.Table.Button.PaginationNextPage'
		);
		this.paginatorService.previousPageLabel = this.localize.transform(
			'Previous Page',
			'Core/System.Table.Button.PaginationPreviousPage'
		);
	}
}
